import React from 'react';

/**
 * "Skip to main content" — the first thing in the tab order on every route.
 *
 * Without it, a keyboard or screen-reader user reaching any article had to tab
 * through the entire header and navigation on every single page load, and the
 * article template offered no landmark to jump to either: it rendered zero
 * <main> elements, so heading navigation was the only way in. WCAG 2.4.1
 * (Bypass Blocks, Level A).
 *
 * The target is resolved defensively rather than trusted. 28 page components
 * bring their own <main>, and a global wrapper would nest landmarks inside them,
 * so `#main-content` is only set on the highest-traffic templates. Everywhere
 * else this falls back to the first <main>, then to #root — the link works on
 * every route rather than only the ones that were remembered.
 *
 * Visually hidden until focused, so it costs sighted readers nothing.
 */
const SkipLink: React.FC = () => {
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const target =
      document.getElementById('main-content') ||
      document.querySelector('main') ||
      document.getElementById('root');
    if (!target) return; // let the href do whatever it can
    e.preventDefault();
    // Containers are not focusable by default; -1 makes them programmatically
    // focusable without adding them to the tab order.
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    (target as HTMLElement).focus({ preventScroll: true });
    target.scrollIntoView({ block: 'start' });
  };

  return (
    <a
      href="#main-content"
      onClick={handleClick}
      className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[9999] focus:rounded-md focus:bg-white focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-blue-700 focus:shadow-lg focus:ring-2 focus:ring-blue-600"
    >
      Skip to main content
    </a>
  );
};

export default SkipLink;
